import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Row,
} from "reactstrap";

import { adminPaths } from "admin/paths";
import { normalizeSlug } from "admin/contentUtils";
import AdminFormActions from "components/admin/AdminFormActions";
import AdminPageHeader from "components/admin/AdminPageHeader";
import { legalDocumentsRepo } from "data/repositories/legalDocumentsRepo";

function emptyDoc() {
  return {
    id: "",
    slug: "",
    title: "",
    subtitle: "",
    intro: "",
    sectionsJson: "[]",
    lastUpdated: "",
    contactEmail: "destek@destinasyon360.com",
    controller: "Karadeniz Dernekleri Konfederasyonu (KADEKON)",
    sortOrder: 0,
    isActive: true,
  };
}

function toForm(doc) {
  return {
    ...emptyDoc(),
    ...doc,
    sectionsJson: JSON.stringify(doc.sections || [], null, 2),
    isActive: doc.isActive !== false,
  };
}

export default function LegalDocumentEdit() {
  const navigate = useNavigate();
  const params = useParams();
  const docId = params.id;
  const isNew = docId === "new" || !docId;

  const [form, setForm] = useState(emptyDoc());
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setError("");
    if (isNew) {
      setForm(emptyDoc());
      setErrors({});
      return;
    }
    const current = legalDocumentsRepo.getById(docId);
    if (!current) {
      setError("Yasal metin bulunamadı");
      return;
    }
    setForm(toForm(current));
    setErrors({});
  }, [isNew, docId]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const onSave = (e) => {
    e.preventDefault();
    setError("");
    const nextErrors = {};
    const slug = normalizeSlug(form.slug || form.id || form.title);
    if (!slug) nextErrors.slug = "Slug zorunludur.";
    else if (isNew && legalDocumentsRepo.getById(slug)) nextErrors.slug = "Bu slug zaten var.";
    if (!form.title.trim()) nextErrors.title = "Başlık zorunludur.";

    let sections = [];
    try {
      sections = JSON.parse(form.sectionsJson || "[]");
      if (!Array.isArray(sections)) throw new Error("sections dizi olmalı");
    } catch (parseError) {
      nextErrors.sectionsJson = "Bölümler geçerli JSON dizi olmalı.";
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    const payload = {
      id: slug,
      slug,
      title: form.title.trim(),
      subtitle: form.subtitle.trim(),
      intro: form.intro.trim(),
      sections,
      lastUpdated: form.lastUpdated.trim(),
      contactEmail: form.contactEmail.trim(),
      controller: form.controller.trim(),
      sortOrder: Number(form.sortOrder) || 0,
      isActive: Boolean(form.isActive),
    };

    try {
      setSaving(true);
      if (isNew) legalDocumentsRepo.create(payload);
      else legalDocumentsRepo.update(docId, payload);
      navigate(`/admin${adminPaths.content.legal.list}`, {
        state: { status: { color: "success", message: "Yasal metin kaydedildi." } },
      });
    } catch (saveError) {
      setError(saveError?.message || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <AdminPageHeader
        gradient="primary"
        title={isNew ? "Yeni yasal metin" : "Yasal metin düzenle"}
        description="Portal /yasal/{slug} sayfasında yayınlanır."
        backAction={() => navigate(`/admin${adminPaths.content.legal.list}`)}
      />

      <Container className="mt--6" fluid>
        <Row>
          <Col xl="10">
            <Card className="shadow">
              <CardHeader className="border-0">
                <h3 className="mb-0">Detay</h3>
              </CardHeader>
              <CardBody>
                {error ? <Alert color="danger">{error}</Alert> : null}
                <Form onSubmit={onSave}>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label>Başlık</Label>
                        <Input
                          value={form.title}
                          invalid={Boolean(errors.title)}
                          onChange={(e) => setField("title", e.target.value)}
                        />
                        <FormFeedback>{errors.title}</FormFeedback>
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label>Slug (URL)</Label>
                        <Input
                          value={form.slug}
                          disabled={!isNew}
                          invalid={Boolean(errors.slug)}
                          onChange={(e) => setField("slug", normalizeSlug(e.target.value))}
                          placeholder="gizlilik-politikasi"
                        />
                        <FormFeedback>{errors.slug}</FormFeedback>
                      </FormGroup>
                    </Col>
                    <Col md="12">
                      <FormGroup>
                        <Label>Alt başlık</Label>
                        <Input value={form.subtitle} onChange={(e) => setField("subtitle", e.target.value)} />
                      </FormGroup>
                    </Col>
                    <Col md="12">
                      <FormGroup>
                        <Label>Giriş metni</Label>
                        <Input
                          type="textarea"
                          rows={4}
                          value={form.intro}
                          onChange={(e) => setField("intro", e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup>
                        <Label>Son güncelleme</Label>
                        <Input
                          value={form.lastUpdated}
                          onChange={(e) => setField("lastUpdated", e.target.value)}
                          placeholder="5 Eylül 2026"
                        />
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup>
                        <Label>İletişim e-posta</Label>
                        <Input
                          value={form.contactEmail}
                          onChange={(e) => setField("contactEmail", e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup>
                        <Label>Sıra</Label>
                        <Input
                          type="number"
                          value={form.sortOrder}
                          onChange={(e) => setField("sortOrder", e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="12">
                      <FormGroup>
                        <Label>Veri sorumlusu</Label>
                        <Input
                          value={form.controller}
                          onChange={(e) => setField("controller", e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="12">
                      <FormGroup check className="mb-3">
                        <Input
                          id="legal-active"
                          type="checkbox"
                          checked={Boolean(form.isActive)}
                          onChange={(e) => setField("isActive", e.target.checked)}
                        />
                        <Label check htmlFor="legal-active">
                          Yayında
                        </Label>
                      </FormGroup>
                    </Col>
                    <Col md="12">
                      <FormGroup>
                        <Label>
                          Bölümler (JSON) — {"{"} title, paragraphs[], items[] {"}"}
                        </Label>
                        <Input
                          type="textarea"
                          rows={16}
                          value={form.sectionsJson}
                          invalid={Boolean(errors.sectionsJson)}
                          onChange={(e) => setField("sectionsJson", e.target.value)}
                          style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 13 }}
                        />
                        <FormFeedback>{errors.sectionsJson}</FormFeedback>
                      </FormGroup>
                    </Col>
                  </Row>
                  <AdminFormActions
                    saving={saving}
                    onCancel={() => navigate(`/admin${adminPaths.content.legal.list}`)}
                  />
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}
